import { useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PDFDocument } from "pdf-lib";
import State from "../../../../../../context";
import { theme, Flex, Button, ConfigProvider } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BreadcrumbProposalStep from "../../component/BreadcrumbProposalStep";
import ModalLevelGoalSettings from "../../component/ModalLevelGoalSettings";
import ModalProposalQuestionnaire from "../../component/ModalProposalQuestionnaire";
import ProposalPdfContentHolderBX from "../../../../../bx/pdf/proposal";
import ProposalPdfContentHolderLevel from "../../pdf";
import SectionAskAdvice from "../../../../../../organism/SectionAskAdvice";
import ViewAllGoal from "../../component/ViewAllGoal";
import ViewOneGoal from "../../component/ViewOneGoal";

import StyledContainer from "./style";

import { PROPOSAL_DEFAULT_DATA_LEVEL } from "../../constant";
import { PDF_PAGE_LIST } from "../../pdf/constant";
import { DEFAULT_BIRTH_YEAR } from "../../../../../../utils/constant";

import { getHtml2pdfPromiseList } from "../../../../../../utils/pdf";
import {
  isAdminView,
  isLockedByAdminView,
  isOrganizationBx,
} from "../../../../../../utils/helper/specialized";
import { addGoal } from "../../request";
import { updateProductObjective } from "../../../../../../utils/request/regularApp";
import { getValidProductId } from "../../helper";

import { ReactComponent as Penny } from "../../image/icon_penny.svg";
import { faClose } from "@fortawesome/free-solid-svg-icons";

const PageLevelProposal = () => {
  const [state, setState] = useContext(State);
  const { token } = theme.useToken();
  const [searchParams] = useSearchParams();

  const guideInitialData =
    state.getPreferenceValue("leadInitial") ??
    state.getPreferenceValue("guideInitialData");

  const productMap = state.getPreferenceValue("productMap");

  useEffect(() => {
    if (state.loading || state.showOverlay) return;

    if (searchParams.get("modeDownloadPdf") && !state.closeAfterPdfGenerated) {
      handleDownloadProposal(searchParams.get("goalId"));
      state.setKeyValue("closeAfterPdfGenerated", true);
      return;
    }

    if (searchParams.get("goalId")) {
      handleViewGoal(searchParams.get("goalId"));
      return;
    }

    if (!state.searchParamHandledShowQuestionnaire) {
      state.setKeyValue("searchParamHandledShowQuestionnaire", true);

      if (searchParams.get("showQuestionnaire") === "true" && !productMap) {
        state.setKeyValue("openModalProposalQuestionnaire", true);
      } else {
        state.setKeyValue("viewMode", productMap ? "goals" : "goal");
      }
    }
  }, [state.loading, state.showOverlay, searchParams]);

  useEffect(() => {
    if (state.loading) return;

    const prospectObjective = state.getPreferenceValue("prospectObjective");

    // handle RTQ quiz
    if (!prospectObjective?.passedRTQ) return;

    state.setKeyValue("loading", true);
    state.setKeyValue("viewMode", "goal");

    // clean "passedRTQ" prop, to prevent handling on the next user entrance
    state
      .setPreference({
        prospectObjective: {
          ...prospectObjective,
          passedRTQ: null,
        },
      })
      .catch(error => state.showError(error))
      .finally(() => state.setKeyValue("loading", false));
  }, [state]);

  useEffect(() => {
    if (state.generatePdfMode && !state.loading) {
      state.setKeyValue("generatePdfMode", false);

      downloadReport();
    }
  }, [state]);

  const downloadReport = async () => {
    const goalLastSavedProps =
      productMap &&
      productMap[state.activeGoal] &&
      productMap[state.activeGoal].at(-1);

    const pagesList = PDF_PAGE_LIST.filter(
      it => it.type === goalLastSavedProps?.investmentObjective
    );

    if (!pagesList.length) return;

    const reportName = isOrganizationBx(state.organization?.name)
      ? "BX Partners Report.pdf"
      : "Level Report.pdf";

    const validProductId = getValidProductId({
      organization: state.organization?.name,
      productsList: state.productsList,
      proposalProductId: goalLastSavedProps.productId,
      proposalRiskTolerance: goalLastSavedProps.riskTolerance?.toLowerCase(),
    });

    const remotePdfUrls = [];

    // get remote urls from the Structure of the Model
    state.productsList
      .find(it => it._id === validProductId)
      ?.Structure?.map(it => {
        const etfData = state.productsList.find(
          product => product.Code === it.ticker
        );

        if (etfData?.EtfUrl) {
          remotePdfUrls.push(etfData.EtfUrl);
        }
      });

    try {
      // generate dynamic PDF from HTML
      const html2pdfInstance = getHtml2pdfPromiseList(pagesList);
      const generatedPdfBuffer =
        await html2pdfInstance.outputPdf("arraybuffer");
      const generatedDoc = await PDFDocument.load(generatedPdfBuffer);

      const mergedDoc = await PDFDocument.create();

      const genPages = await mergedDoc.copyPages(
        generatedDoc,
        generatedDoc.getPageIndices()
      );
      genPages.forEach(page => mergedDoc.addPage(page));

      // add remote PDFs
      for (const url of remotePdfUrls) {
        const response = await fetch(url);
        const remoteBuffer = await response.arrayBuffer();
        const remoteDoc = await PDFDocument.load(remoteBuffer);
        const remotePages = await remoteDoc.getPages();

        for (const page of remotePages) {
          const { width: srcWidth, height: srcHeight } = page.getSize();

          const firstGeneratedPage = generatedDoc.getPage(0);
          const { width: targetWidth, height: targetHeight } =
            firstGeneratedPage.getSize();

          const scaleX = targetWidth / srcWidth;
          const scaleY = targetHeight / srcHeight;
          const scale = Math.min(scaleX, scaleY);

          const embeddedPage = await mergedDoc.embedPage(page);
          const newPage = mergedDoc.addPage([targetWidth, targetHeight]);

          newPage.drawPage(embeddedPage, {
            x: (targetWidth - srcWidth * scale) / 2,
            y: (targetHeight - srcHeight * scale) / 2,
            xScale: scale,
            yScale: scale,
          });
        }
      }

      // download merged PDF
      const mergedBytes = await mergedDoc.save();
      const blob = new Blob([mergedBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = reportName;
      link.click();
      URL.revokeObjectURL(url);

      state.closeAfterPdfGenerated && window.close();
    } catch (error) {
      state.showError(error);
    } finally {
      state.setKeyValue("showOverlay", false);
    }
  };

  const getBreadcrumbActiveStep = () => {
    if (state.activeSection === "reviewProposal") return "review";

    return "select";
  };

  const getLabelProposalFor = () => {
    if (guideInitialData?.isMarried) {
      return `${state.personalInfo?.firstName} & ${guideInitialData.spouseFirstName} ${state.personalInfo?.lastName}`;
    } else {
      return `${state.personalInfo?.firstName} ${state.personalInfo?.lastName}`;
    }
  };

  const handleAddNewGoal = newGoalData => {
    state.setKeyValue("loading", true);

    addGoal({
      email: state._id,
      proposalData: {
        ...PROPOSAL_DEFAULT_DATA_LEVEL,
        ...newGoalData,
        productId: getValidProductId({
          organization: state.organization?.name,
          productsList: state.productsList,
          proposalRiskTolerance: newGoalData.riskTolerance?.toLowerCase(),
        }),
      },
    })
      .then(() =>
        state.setUserData().then(() => {
          state.setKeyValue("openModalLevelGoalSettings", false);
          state.setKeyValue("loading", false);
          state.showSuccess("New Goal Added!");
        })
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const handleDownloadProposal = goalId => {
    state.setKeyValue("activeGoal", goalId);
    state.setKeyValue("showOverlay", true);
    state.setKeyValue("generatePdfMode", true);
  };

  const handleViewGoal = goalId => {
    state.setKeyValue("showCompareMode", false);
    state.setKeyValue("activeGoal", goalId);
    state.setKeyValue("viewMode", "goal");
  };

  const onClickProposalClose = () => {
    if (state.viewMode === "goals") {
      window.close();
    } else {
      state.setKeyValue("viewMode", "goals");
      state.setKeyValue("activeSection", "goalDetails");
      state.setKeyValue("showCompareMode", false);
      state.setKeyValue("compareProducts", [state.selectedProduct]);
    }
  };

  const submitProposalQuestionnaire = proposalData => {
    state.setKeyValue("loading", true);
    state.setKeyValue("openModalProposalQuestionnaire", false);

    const requestBody = {
      ...proposalData,
      email: state._id,
      birthYear: DEFAULT_BIRTH_YEAR,
      riskTolerance:
        proposalData.riskTolerance === "unknown"
          ? "moderate"
          : proposalData.riskTolerance,
    };

    updateProductObjective({
      requestBody,
      userEmail: state._id,
    })
      .then(() => {
        state.setUserData().then(() => {
          state.setKeyValue("activeGoal", undefined);
          state.setKeyValue("loading", false);
          state.setKeyValue("viewMode", "goal");
          state.showSuccess("New proposal created!");
        });
      })
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            activeBarBorderWidth: 0,
            itemSelectedBg: token.colorPrimary,
            itemSelectedColor: "#fff",
            itemHoverBg: token.colorPrimary,
            itemHoverColor: "#fff",
          },
        },
      }}
    >
      <StyledContainer token={token}>
        <header>
          <article
            style={{
              background: token.proposal_header__background,
              borderBottom: "1px solid #15212A",
            }}
          >
            <img alt="logo" src={token.logo_src} />
            <Button
              icon={<Penny />}
              onClick={() => state.setKeyValue("openModalAskAdvice", true)}
              shape="round"
              style={{ background: "#DAE2E5" }}
              type="text"
            >
              <span>
                Ask<b>Penny</b>
              </span>
            </Button>
          </article>

          <article style={{ background: "rgba(22, 36, 45, 0.25)" }}>
            <h4>
              <span
                style={{
                  color: "#CFD9E3",
                  fontWeight: 400,
                }}
              >
                Proposal:{" "}
              </span>
              {getLabelProposalFor()}
            </h4>

            <Flex align="center" gap={32}>
              <BreadcrumbProposalStep activeStep={getBreadcrumbActiveStep()} />

              {productMap &&
              (state.viewMode === undefined || state.viewMode === "goals") ? (
                <Button
                  disabled={isLockedByAdminView({
                    managerAccess: state.managerAccess,
                  })}
                  onClick={() =>
                    state.setKeyValue("openModalLevelGoalSettings", true)
                  }
                  shape="round"
                  style={{ background: "#84B8C8" }}
                  type="text"
                >
                  New Proposal
                </Button>
              ) : !state.isProposalDirty && productMap ? (
                <Button
                  disabled={state.activeSection === "reviewProposal"}
                  onClick={() =>
                    state.setKeyValue("activeSection", "reviewProposal")
                  }
                  shape="round"
                  style={{ background: "#84B8C8" }}
                  type="text"
                >
                  Review Proposal
                </Button>
              ) : (
                <Button
                  disabled={isAdminView()}
                  onClick={() =>
                    state.setKeyValue("openModalConfirmSaveProposal", true)
                  }
                  shape="round"
                  style={{ background: "#84B8C8" }}
                  type="text"
                >
                  Save Proposal
                </Button>
              )}

              {
                // allow to close one proposal view only if we have already saved proposals
                productMap && (
                  <Button
                    icon={
                      <FontAwesomeIcon
                        icon={faClose}
                        style={{ color: "#052C2B" }}
                      />
                    }
                    onClick={onClickProposalClose}
                    shape="circle"
                    style={{ border: "unset" }}
                  />
                )
              }
            </Flex>
          </article>
        </header>

        <main>
          {productMap && state.viewMode === "goals" && (
            <ViewAllGoal
              handleDownloadProposal={handleDownloadProposal}
              handleViewGoal={handleViewGoal}
            />
          )}

          {state.viewMode === "goal" && (
            <ViewOneGoal handleDownloadProposal={handleDownloadProposal} />
          )}
        </main>

        {isOrganizationBx(state.organization?.name) ? (
          <ProposalPdfContentHolderBX />
        ) : (
          <ProposalPdfContentHolderLevel />
        )}

        <ModalLevelGoalSettings
          onCancel={() =>
            state.setKeyValue("openModalLevelGoalSettings", false)
          }
          onSuccess={handleAddNewGoal}
          open={state.openModalLevelGoalSettings}
        />
        <ModalProposalQuestionnaire
          handleClose={() => {}}
          onSuccess={submitProposalQuestionnaire}
          open={state.openModalProposalQuestionnaire}
          userName={`${state.personalInfo?.firstName} ${state.personalInfo?.lastName}`}
        />
        <SectionAskAdvice state={state} setState={setState} />
      </StyledContainer>
    </ConfigProvider>
  );
};

export default PageLevelProposal;
